import axios from '@/axiosInterceptor';
import dayjs from 'dayjs';
import _ from 'lodash';

const state = () => ({
  results: { results: [] },
  results_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  search_query: {},
  search_error: {},
  does_not_meet_requirements: false,
  is_load_board_loading: false,
  is_load_details_loading: false,
  load_details: {},
  load_capacity_results: [],
  load_capacity_results_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  load_capacity_results_error: {},
  is_capacity_search_loading: false,
  capacity_search_params: {},
  is_truck_postings_loading: false,
  truck_postings: [],
  truck_postings_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  truck_posting: {},
  carrier_disposition: {},
  carrier_disposition_results: [],
  equipment_types: [],
  is_post_loading: false,
  loadSearchFilters: {},
  all_results: {
    results: [],
    pagination: {
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
    },
    query: '',
  },
  posted_results: {
    results: [],
    pagination: {
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
    },
    query: '',
  },
  unposted_results: {
    results: [],
    pagination: {
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
    },
    query: '',
  },
  bulk_post_errors: {},
  search_loads_quiet_loading: false,
  prefetched_load: {},
  offers: [],
  offers_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  is_offers_loading: false,
  history_offers: [],
  history_offers_pagination: {
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
  },
  is_counter_offer_loading: false,
  is_accept_offer_loading: false,
  is_reject_offer_loading: false,
});

// helpers
const sortHash = {
  pickup_asc: 'pickup_time asc',
  pickup_desc: 'pickup_time desc',
  dropoff_asc: 'dropoff_time asc',
  dropoff_desc: 'dropoff_time desc',
  load_id_asc: 'external_load_number asc',
  load_id_desc: 'external_load_number desc',
  weight_asc: 'weight asc',
  weight_desc: 'weight desc',
  origin_asc: 'origin_name asc',
  origin_desc: 'origin_name desc',
  destination_asc: 'destination_name asc',
  destination_desc: 'destination_name desc',
  equipment_asc: 'posted_equipment_type_string asc',
  equipment_desc: 'posted_equipment_type_string desc',
};

const isValidNumber = (value) => {
  return !isNaN(Number(value)) && Number(value) !== 0;
};

const buildLoadSearchQueryParams = (query, sort) => {
  let params = {
    status_in: 'available',
  };

  if (sort) {
    params.s = sortHash[sort];
  } else {
    delete params.s;
  }

  if (query.view === 'all') {
    delete params.can_be_posted_true;
  } else if (query.view === 'posted') {
    params.can_be_posted_true = 1;
  } else if (query.view === 'unposted') {
    params.can_be_posted_true = 0;
  }

  if (query.my_loads) {
    params.owner_user_only = true;
  } else {
    params.owner_user_only = false;
  }

  if (query.load_id) {
    params.external_load_number_cont = query.load_id;
  } else {
    delete params.external_load_number_cont;
  }

  if (query.equipment && query?.equipment?.length > 0) {
    params.equipment_type_in = query.equipment;
  } else {
    delete params.equipment_type_in;
  }
  if (query.trailer && query?.trailer?.length > 0) {
    params.equipment_type_in = query.trailer;
  } else if (!query.equipment) {
    delete params.equipment_type_in;
  }

  if (query.min_rate) {
    params.min_rate_gteq = parseInt(query.min_rate);
  }
  if (query.weight) {
    params.weight_gteq = parseInt(query.weight);
    params.weight_unit_eq = 'lbs';
  } else {
    delete params.weight_gteq;
    delete params.weight_unit_eq;
  }

  if (query.trailer_length) {
    params.trailer_length_gteq = parseInt(query.trailer_length);
    // params.trailer_length_unit_eq = 'ft';
  } else {
    delete params.trailer_length_gteq;
    // delete params.trailer_length_unit_eq;
  }

  if (query.origin_area_id && isValidNumber(query.origin_area_id)) {
    params.origin_area_id = query.origin_area_id;

    if (!params.origin_deadhead) {
      params.origin_deadhead_distance_miles = '50';
    }
  } else {
    delete params.origin_deadhead_distance_miles;
  }

  if (query.destination_area_id && isValidNumber(query.destination_area_id)) {
    params.destination_area_id = query.destination_area_id;

    if (!params.destination_deadhead) {
      params.destination_deadhead_distance_miles = '50';
    }
  } else {
    delete params.destination_deadhead_distance_miles;
  }

  if (query.pickup_start) {
    params.start_stop_scheduled_window_started_at_date_gteq = dayjs(
      query.pickup_start
    ).format('YYYY-MM-DD');
    params.start_stop_scheduled_window_started_at_date_lteq = dayjs(
      query.pickup_start
    ).format('YYYY-MM-DD');
  } else {
    params.start_stop_scheduled_window_started_at_date_gteq =
      dayjs().format('YYYY-MM-DD');
  }
  // TODO --- look into if this is needed
  if (query.pickup_end) {
    params.start_stop_scheduled_window_started_at_date_lteq = dayjs(
      query.pickup_end
    ).format('YYYY-MM-DD');
  }

  if (query.dropoff_start) {
    params.end_stop_scheduled_window_started_at_date_gteq = dayjs(
      query.dropoff_start
    ).format('YYYY-MM-DD');
    params.end_stop_scheduled_window_started_at_date_lteq = dayjs(
      query.dropoff_start
    ).format('YYYY-MM-DD');
  }

  if (query.dropoff_end) {
    params.end_stop_scheduled_window_started_at_date_lteq = dayjs(
      query.dropoff_end
    ).format('YYYY-MM-DD');
  }

  if (query.deadhead) {
    params.origin_deadhead_distance_miles = parseInt(query.deadhead);
  }

  if (query.origin_deadhead) {
    params.origin_deadhead_distance_miles = query.origin_deadhead;
  }

  if (query.destination_deadhead) {
    params.destination_deadhead_distance_miles = query.destination_deadhead;
  }

  if (query.teams && query.teams?.length > 0) {
    if (query.teams?.includes('all')) {
      delete params.team_id_in;
    } else {
      params.team_id_in = query.teams;
    }
  } else {
    delete params.team_id_in;
  }

  if (query.company_wide_view) {
    params.company_wide_view = true;
  } else {
    params.company_wide_view = false;
  }

  if (query.posting) {
    if (query?.posting.includes('8hr')) {
      // less than 8hr
      params.last_posted_at_gteq = dayjs().subtract(8, 'hour');
    } else if (query?.posting.includes('12hr')) {
      // between 8 - 12 hours
      params.last_posted_at_lteq = dayjs().subtract(8, 'hour');
      params.last_posted_at_gteq = dayjs().subtract(12, 'hour');
    } else if (query?.posting.includes('24hr')) {
      // between 12 - 24 hours
      params.last_posted_at_lteq = dayjs().subtract(12, 'hour');
      params.last_posted_at_gteq = dayjs().subtract(24, 'hour');
    } else if (query?.posting.includes('72hr')) {
      // between 24 - 72 hours
      params.last_posted_at_lteq = dayjs().subtract(24, 'hour');
      params.last_posted_at_gteq = dayjs().subtract(72, 'hour');
    } else if (query?.posting.includes('>72hr')) {
      // greater than 72 hours
      params.last_posted_at_lteq = dayjs().subtract(72, 'hour');
    }
  }

  if (query.status) {
    params.broker_connection_status = 'In Network';
    // if (query.status?.includes('In Network')) {
    //   params.broker_connection_status = 'In Network';
    // } else {
    //   params.broker_connection_status = 'Out of Network';
    // }
  } else {
    delete params.broker_connection_status;
  }

  if (query.certifications) {
    if (query.certifications?.includes('hazmat')) {
      params.is_hazmat_required_true = 1;
    } else {
      delete params.is_hazmat_required_true;
    }

    if (query.certifications?.includes('tanker')) {
      params.is_taker_required_true = 1;
    } else {
      delete params.is_taker_required_true;
    }

    if (query.certifications?.includes('none')) {
      delete params.is_hazmat_required_true;
      delete params.is_taker_required_true;
    }
  } else {
    delete params.is_hazmat_required_true;
    delete params.is_taker_required_true;
  }

  // if (query.searchQuery) {
  //   params.broker_legal_name_cont = query.searchQuery;
  // } else if (!query.searchQuery) {
  //   delete params.broker_legal_name_cont;
  // }
  if (query.company_id) {
    params.company_id_eq = query.company_id;
  } else if (!query.company_id) {
    delete params.company_id_eq;
  }

  if (query.team) {
    params.is_teams_required_true = 1;
  } else if (!query.equipment) {
    delete params.is_teams_required_true;
  }

  return params;
};

const loadMatchSortHash = {
  equipment_age: 'equipment_age desc',
  origin_deadhead_asc: 'origin_deadhead asc',
  deadhead_destination_asc: 'destination_deadhead asc',
  total_deadhead: 'total_deadhead asc',
  fleet_size_desc: 'fleet_size desc',
};

// actions
const actions = {
  async searchLoads(
    { commit, state },
    {
      page = 1,
      perPage = 50,
      query = {},
      sort = '',
      initial = false,
      refetch = false,
      quiet = false,
    }
  ) {
    if (!initial || (initial && query?.view === 'all')) {
      if (!quiet) {
        commit('setLoadboardLoading', true);
      } else {
        commit('setQuietLoading', true);
      }
    }

    console.log('Load Search Query: ', query);
    console.log('Load Search Sort: ', sort);

    const q = { ...query, ...sort };
    if (query.view && refetch) {
      switch (query.view) {
        case 'all': {
          if (!_.isEqual(q, state.all_results?.query)) {
            break;
          } else {
            commit('setLoads', state.all_results);
            commit('setLoadboardPagination', state.all_results?.pagination);
            commit('setLoadboardLoading', false);
            return;
          }
        }
        case 'posted': {
          if (!_.isEqual(q, state.posted_results?.query)) {
            break;
          } else {
            commit('setLoads', state.posted_results);
            commit('setLoadboardPagination', state.posted_results?.pagination);
            commit('setLoadboardLoading', false);
            return;
          }
        }
        case 'unposted': {
          if (!_.isEqual(q, state.unposted_results?.query)) {
            break;
          } else {
            commit('setLoads', state.unposted_results);
            commit(
              'setLoadboardPagination',
              state.unposted_results?.pagination
            );
            commit('setLoadboardLoading', false);
            return;
          }
        }
        default:
          break;
      }
    }

    const body = {
      q: {
        ...buildLoadSearchQueryParams(query, sort),
      },
      page,
      per_page: perPage,
    };

    try {
      // commit('setLoadSearchFilters', query);

      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/loads/broker_search`,
        body
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        const noUserLoads = res?.data?.results?.length === 0 && query?.my_loads;
        if (noUserLoads) {
          return { ...res.data, no_user_loads: true };
        }

        if (!quiet && (!initial || (initial && query?.view === 'all'))) {
          commit('setLoads', res.data);
          commit('setLoadboardPagination', {
            totalRecords: Number(res.headers?.totalitems),
            totalPages: Number(res.headers?.totalpages),
            currentPage: Number(res.headers?.currentpage),
          });
          commit('setLoadsQuery', query);
        }

        if (query?.view) {
          switch (query.view) {
            case 'all': {
              commit('setAllResults', {
                results: res?.data?.results,
                pagination: {
                  totalRecords: Number(res.headers?.totalitems),
                  totalPages: Number(res.headers?.totalpages),
                  currentPage: Number(res.headers?.currentpage),
                },
                query: q,
              });
              break;
            }
            case 'posted': {
              commit('setPostedResults', {
                results: res?.data?.results,
                pagination: {
                  totalRecords: Number(res.headers?.totalitems),
                  totalPages: Number(res.headers?.totalpages),
                  currentPage: Number(res.headers?.currentpage),
                },
                query: q,
              });
              break;
            }
            case 'unposted': {
              commit('setUnpostedResults', {
                results: res?.data?.results,
                pagination: {
                  totalRecords: Number(res.headers?.totalitems),
                  totalPages: Number(res.headers?.totalpages),
                  currentPage: Number(res.headers?.currentpage),
                },
                query: q,
              });
              break;
            }
            default:
              break;
          }
        }

        commit('setSearchLoadError', {});

        return res;
      } else if ([422, 400].includes(res?.status)) {
        commit('setSetNotMeetingRequirements', true);
        commit('setSearchLoadError', res?.data?.error);
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
    } catch (error) {
      if (error?.response?.data?.error) {
        commit('setSetNotMeetingRequirements', true);
        commit('setSearchLoadError', error?.response?.data?.error);
      }
      console.log('error', error);
    } finally {
      commit('setLoadboardLoading', false);
      commit('setQuietLoading', false);
    }
  },
  async getLoadBoardDetail({ commit }, { id, quiet = false }) {
    if (!quiet) {
      commit('setLoadDetailsLoading', true);
    }

    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/loads/${id}/broker_details`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        commit('setLoadDetail', res.data);
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
      return res;
    } catch (e) {
      console.log('error', e);
    } finally {
      commit('setLoadDetailsLoading', false);
    }
  },
  async getCapacitySearchResults(
    { commit },
    { id, page = 1, params = {}, sort = '' }
  ) {
    commit('setCapacitySearchLoading', true);
    commit('setCapacitySearchParams', params);

    let querySort;
    if (loadMatchSortHash[sort]) {
      querySort = loadMatchSortHash[sort];
    }

    const searchParams = {
      page,
      ...params,
      ...(querySort ? { q: { s: querySort } } : {}),
    };

    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/carriers/load_match/${id}`,
        { params: searchParams }
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        commit('setCapacityResults', res.data);

        commit('setCapacityResultsPagination', {
          totalRecords: Number(res.headers?.totalitems),
          totalPages: Number(res.headers?.totalpages),
          currentPage: Number(res.headers?.currentpage),
        });
      } else {
        commit('setCapacityResultsError', res.data);
      }
      return res;
    } catch (e) {
      console.log('error', e);
    } finally {
      commit('setCapacitySearchLoading', false);
    }
  },
  async getCarrierDispositions({ commit }, { query }) {
    // commit('setLoadDetailsLoading', true);

    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/load_carrier_dispositions${query}`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        commit('setCarrierDispositionResults', res.data);
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
      return res;
    } catch (e) {
      console.log('error', e);
    } finally {
      // commit('setLoadDetailsLoading', false);
    }
  },
  async setCarrierDisposition(
    { commit },
    { carrier_id, load_id, reason, notes }
  ) {
    const params = {
      load_carrier_disposition: { carrier_id, load_id, reason, notes },
    };

    try {
      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/load_carrier_dispositions`,
        params
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        commit('setCarrierDispositionData', res.data);
      }
      return res;
    } catch (e) {
      console.log('error', e);
    }
  },
  async reinstateCarrierDisposition(_, { id }) {
    try {
      const res = await axios.delete(
        `${process.env.API_MONITOR}/api/v1/load_carrier_dispositions/${id}`
      );
      return res;
    } catch (e) {
      console.log('error', e);
    }
  },
  async getTruckPostings({ commit }, { query = '' }) {
    commit('setTruckPostingLoading', true);

    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/truck_postings${query}`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        commit('setTruckPostings', res.data);
        commit('setTruckPostingsPagination', {
          totalRecords: Number(res.headers?.totalitems),
          totalPages: Number(res.headers?.totalpages),
          currentPage: Number(res.headers?.currentpage),
        });
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
      return res;
    } catch (e) {
      console.log('error', e);
    } finally {
      commit('setTruckPostingLoading', false);
    }
  },
  async getTruckPostingById({ commit }, { id }) {
    if (!id) return;
    commit('setTruckPostingLoading', true);

    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/truck_postings/${id}`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        commit('setTruckPosting', res.data);
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
      return res;
    } catch (e) {
      console.log('error', e);
    } finally {
      commit('setTruckPostingLoading', false);
    }
  },
  async createTruckPosting(
    { dispatch },
    {
      truck_number,
      origin_area_id,
      destination_area_id,
      start_date,
      origin_deadhead_tolerance,
      destination_deadhead_tolerance,
      origin_search_display_name,
      destination_search_display_name,
    }
  ) {
    // commit('setTruckPostingLoading', true);

    console.log(
      'capacity posting obj: ',
      truck_number,
      origin_area_id,
      destination_area_id,
      start_date,
      origin_deadhead_tolerance,
      destination_deadhead_tolerance,
      origin_search_display_name,
      destination_search_display_name
    );
    // TODO -- hardcoded
    const params = {
      truck_posting: {
        carrier_id: 557441,
        origin_deadhead_tolerance: 150,
        destination_deadhead_tolerance: 150,
        destination_area_id: 25503,
        is_active: true,
        origin_area_id: origin_area_id,
        start_date: '2025-07-20',
        posted_equipment_type: 'reefer',
        carrier_vehicle_id: 295923416,
        origin_search_display_name,
        destination_search_display_name,

        // carrier_vehicle_id: truck_number,
        // origin_area_id,
        // destination_area_id,
        // start_date,
        // deadhead_miles_tolerance: deadhead_miles,
        // is_active: true,
        // // carrier_id: 557441,
      },
    };

    try {
      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/truck_postings`,
        params
      );

      console.log('truck posting res:', res);

      if ([200, 201, 204].includes(res?.status) && res.data) {
        dispatch('getTruckPostings', { query: '?q[is_active_eq]=1' });
      }
      return res;
    } catch (e) {
      console.log('error', e);
      return e;
    } finally {
      // commit('setTruckPostingLoading', false);
    }
  },
  async updateTruckPosting({ dispatch }, { id, body }) {
    // commit('setTruckPostingLoading', true);

    const params = {
      truck_posting: body,
    };

    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/truck_postings/${id}`,
        params
      );

      console.log('truck posting update res:', res);

      if ([200, 201, 204].includes(res?.status) && res.data) {
        dispatch('getTruckPostings', { query: '?q[is_active_eq]=1' });
      }
      return res;
    } catch (e) {
      console.log('error', e);
      return e;
    } finally {
      // commit('setTruckPostingLoading', false);
    }
  },
  async postLoad({ commit, dispatch }, { id }) {
    commit('setPostLoading', true);

    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/loads/${id}/post_load`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        dispatch('getLoadBoardDetail', { id, quiet: true });
      } else {
        return res;
      }
      return res;
    } catch (e) {
      const response = e?.response;

      // If the response is 422, it means the load does not meet posting qualifications
      // and we want to return the specific errors to the calling component
      if (response?.status === 422 && Array.isArray(response?.data?.errors)) {
        return { status: 422, errors: response?.data?.errors };
      }

      console.log('error', e);
    } finally {
      commit('setPostLoading', false);
    }
  },
  async unpostLoad({ commit, dispatch }, { id }) {
    commit('setPostLoading', true);

    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/loads/${id}/unpost_load`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        dispatch('getLoadBoardDetail', { id, quiet: true });
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
      return res;
    } catch (e) {
      console.log('error', e);
    } finally {
      commit('setPostLoading', false);
    }
  },
  async bulkPost({ commit, dispatch, state }, { load_ids }) {
    commit('setPostLoading', true);

    const params = {
      load_ids,
    };

    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/loads/bulk_post`,
        params
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        async function pollLoads(
          { dispatch, commit, state },
          load_ids,
          maxRetries = 4,
          delay = 500
        ) {
          let attempt = 0;

          const run = async () => {
            attempt++;

            const [unpostedRes] = await Promise.all([
              dispatch('searchLoads', {
                query: {
                  ...state.search_query,
                  view: 'unposted',
                },
                quiet: true,
              }),
              dispatch('searchLoads', {
                query: {
                  ...state.search_query,
                  view: 'posted',
                },
                quiet: true,
              }),
              dispatch('searchLoads', {
                query: {
                  ...state.search_query,
                  view: 'all',
                },
                quiet: true,
              }),
            ]);

            const doesIncludeRemoved = unpostedRes?.data?.results?.some(
              (item) => load_ids.includes(item.id)
            );

            if (doesIncludeRemoved && attempt < maxRetries) {
              console.log(
                `Attempt ${attempt}: load(s) still present, retrying...`
              );
              return new Promise((resolve) =>
                setTimeout(() => resolve(run()), delay)
              );
            }

            if (unpostedRes) {
              commit('setLoads', unpostedRes.data);
              commit('setLoadboardPagination', {
                totalRecords: Number(unpostedRes.headers?.totalitems),
                totalPages: Number(unpostedRes.headers?.totalpages),
                currentPage: Number(unpostedRes.headers?.currentpage),
              });
            }
          };

          return run();
        }

        if (res?.data?.success?.length > 0) {
          pollLoads({ dispatch, commit, state }, load_ids);
        }

        if (res?.data?.errors?.length > 0) {
          commit('setBulkPostError', {
            type: 'Post',
            errors: res?.data?.errors,
          });
        }
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
      return res;
    } catch (e) {
      console.log('error', e);
    } finally {
      commit('setPostLoading', false);
    }
  },
  async bulkUnpost({ commit, dispatch, state }, { load_ids }) {
    commit('setPostLoading', true);

    const params = {
      load_ids,
    };

    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/loads/bulk_unpost`,
        params
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        async function pollLoads(
          { dispatch, commit, state },
          load_ids,
          maxRetries = 4,
          delay = 500
        ) {
          let attempt = 0;

          const run = async () => {
            attempt++;

            const [postedRes] = await Promise.all([
              dispatch('searchLoads', {
                query: {
                  ...state.search_query,
                  view: 'posted',
                },
                quiet: true,
              }),
              dispatch('searchLoads', {
                query: {
                  ...state.search_query,
                  view: 'unposted',
                },
                quiet: true,
              }),
              dispatch('searchLoads', {
                query: {
                  ...state.search_query,
                  view: 'all',
                },
                quiet: true,
              }),
            ]);

            const doesIncludeRemoved = postedRes?.data?.results?.some((item) =>
              load_ids.includes(item.id)
            );

            if (doesIncludeRemoved && attempt < maxRetries) {
              console.log(
                `Attempt ${attempt}: load(s) still present, retrying...`
              );
              return new Promise((resolve) =>
                setTimeout(() => resolve(run()), delay)
              );
            }

            if (postedRes) {
              commit('setLoads', postedRes.data);
              commit('setLoadboardPagination', {
                totalRecords: Number(postedRes.headers?.totalitems),
                totalPages: Number(postedRes.headers?.totalpages),
                currentPage: Number(postedRes.headers?.currentpage),
              });
            }
          };

          return run();
        }

        if (res?.data?.success?.length > 0) {
          pollLoads({ dispatch, commit, state }, load_ids);
        }

        if (res?.data?.errors?.length > 0) {
          commit('setBulkPostError', {
            type: 'Unpost',
            errors: res?.data?.errors,
          });
        }
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
      return res;
    } catch (e) {
      console.log('error', e);
    } finally {
      commit('setPostLoading', false);
    }
  },
  async getEquipmentTypes({ commit }) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/loads/posted_equipment_types_with_descriptions`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        commit('setEquipmentTypes', res.data);
      } else {
        throw new Error('No data returned from API', { cause: res });
      }
      return res;
    } catch (e) {
      console.log('error', e);
    }
  },
  async getOffers({ commit, state }, { page = 1, refetch = false }) {
    if (!refetch) {
      commit('setGetOffersLoading', true);
    }

    const params = {
      page,
      per_page: 50,
      'q[s]': 'created_at desc',
      // 'q[status_eq]': 'pending', // revisit when we have more offers to work with in staging
      'q[expires_at_not_null]': 1,
    };

    try {
      const res = await axios.get(`${process.env.API_MONITOR}/api/v1/offers`, {
        params,
      });

      if ([200, 201, 204].includes(res?.status) && res.data) {
        if (refetch) {
          const combinedOffers = [...state.active_offers, ...res.data];
          commit('setOffers', combinedOffers);
        } else {
          commit('setOffers', res.data);
        }

        commit('setOffersPagination', {
          totalRecords: Number(res.headers?.totalitems),
          totalPages: Number(res.headers?.totalpages),
          currentPage: Number(res.headers?.currentpage),
        });
      } else {
        //
      }
      return res;
    } catch (e) {
      console.log('error', e);
    } finally {
      commit('setGetOffersLoading', false);
    }
  },
  async getOffersHistory({ commit, state }, { page = 1, refetch = false }) {
    try {
      const res = await axios.get(
        `${process.env.API_MONITOR}/api/v1/offers?page=${page}&per_page=50&q[s]=created_at desc&q[status_not_eq]=pending`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        if (refetch) {
          const combinedOffers = [...state.active_offers, ...res.data];
          commit('setHistoryOffers', combinedOffers);
        } else {
          commit('setHistoryOffers', res.data);
        }

        commit('setHistoryOffersPagination', {
          totalRecords: Number(res.headers?.totalitems),
          totalPages: Number(res.headers?.totalpages),
          currentPage: Number(res.headers?.currentpage),
        });
      }
      return res;
    } catch (e) {
      console.log('error', e);
    }
  },
  async counterOffer({ dispatch, commit }, { id, rate }) {
    commit('setCounterOfferLoading', true);

    try {
      const res = await axios.post(
        `${process.env.API_MONITOR}/api/v1/offers/${id}/counter`,
        { rate }
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        dispatch('getOffers', {});
      }
      return res;
    } catch (e) {
      console.log('error', e);
      return e;
    } finally {
      commit('setCounterOfferLoading', false);
    }
  },
  async acceptOffer({ dispatch, commit }, { id }) {
    commit('setAcceptOfferLoading', true);

    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/offers/${id}/accept`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        dispatch('getOffers', {});
      }
      return res;
    } catch (e) {
      console.log('error', e);
      return e;
    } finally {
      commit('setAcceptOfferLoading', false);
    }
  },
  async rejectOffer({ dispatch, commit }, { id }) {
    commit('setRejectOfferLoading', true);

    try {
      const res = await axios.patch(
        `${process.env.API_MONITOR}/api/v1/offers/${id}/reject`
      );

      if ([200, 201, 204].includes(res?.status) && res.data) {
        dispatch('getOffers', {});
      }
      return res;
    } catch (e) {
      console.log('error', e);
      return e;
    } finally {
      commit('setRejectOfferLoading', false);
    }
  },
};

// mutations
const mutations = {
  setLoads(state, data) {
    state.results = data;
  },
  setLoadboardPagination(state, headers) {
    state.results_pagination = headers;
  },
  setLoadsQuery(state, data) {
    state.search_query = data;

    localStorage.setItem('load_search_filters', JSON.stringify(data));
  },
  setSearchLoadError(state, data) {
    state.search_error = data;
  },
  setSetNotMeetingRequirements(state, bool) {
    state.does_not_meet_requirements = bool;
  },
  setLoadboardLoading(state, bool) {
    state.is_load_board_loading = bool;
  },
  setLoadDetailsLoading(state, data) {
    state.is_load_details_loading = data;
  },
  setLoadDetail(state, data) {
    state.load_details = data;
  },
  setCapacityResults(state, data) {
    state.load_capacity_results = data;
  },
  setCapacityResultsPagination(state, headers) {
    state.load_capacity_results_pagination = headers;
  },
  setCapacityResultsError(state, data) {
    state.load_capacity_results_error = data;
  },
  setCapacitySearchLoading(state, bool) {
    state.is_capacity_search_loading = bool;
  },
  setCapacitySearchParams(state, data) {
    state.capacity_search_params = data;
  },
  setTruckPostingLoading(state, bool) {
    state.is_truck_postings_loading = bool;
  },
  setTruckPostings(state, data) {
    state.truck_postings = data;
  },
  setTruckPostingsPagination(state, headers) {
    state.truck_postings_pagination = headers;
  },
  setTruckPosting(state, data) {
    state.truck_posting = data;
  },
  setCarrierDispositionData(state, data) {
    state.carrier_disposition = data;
  },
  setCarrierDispositionResults(state, data) {
    state.carrier_disposition_results = data;
  },
  setEquipmentTypes(state, data) {
    state.equipment_types = data;
  },
  setPostLoading(state, bool) {
    state.is_post_loading = bool;
  },
  setLoadSearchFilters(state, data) {
    state.loadSearchFilters = data;
  },
  setAllResults(state, data) {
    state.all_results = data;
  },
  setPostedResults(state, data) {
    state.posted_results = data;
  },
  setUnpostedResults(state, data) {
    state.unposted_results = data;
  },
  setBulkPostError(state, data) {
    state.bulk_post_errors = data;
  },
  setQuietLoading(state, bool) {
    state.search_loads_quiet_loading = bool;
  },
  setPrefetchedLoad(state, data) {
    state.prefetched_load = data;
  },
  setOffers(state, data) {
    state.offers = data;
  },
  setGetOffersLoading(state, bool) {
    state.is_offers_loading = bool;
  },
  setOffersPagination(state, headers) {
    state.offers_pagination = headers;
  },
  setHistoryOffers(state, data) {
    state.history_offers = data;
  },
  setHistoryOffersPagination(state, headers) {
    state.history_offers_pagination = headers;
  },
  setCounterOfferLoading(state, bool) {
    state.is_counter_offer_loading = bool;
  },
  setAcceptOfferLoading(state, bool) {
    state.is_accept_offer_loading = bool;
  },
  setRejectOfferLoading(state, bool) {
    state.is_reject_offer_loading = bool;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
